import React from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { getCurrencySymbol } from '../utils/pricingUtils';

interface PricingValueInputProps {
  pricingType: string;
  value: number;
  description?: string;
  currency: string;
  onValueChange: (value: number) => void;
  onDescriptionChange: (description: string) => void;
  className?: string;
}

export function PricingValueInput({
  pricingType,
  value,
  description,
  currency,
  onValueChange,
  onDescriptionChange,
  className
}: PricingValueInputProps) {
  const currencySymbol = getCurrencySymbol(currency);
  const isPercentage = pricingType === 'percentage';

  const getInputLabel = () => {
    switch (pricingType) {
      case 'fixed':
        return 'Fixed Amount';
      case 'percentage':
        return 'Percentage';
      case 'per_person':
        return 'Amount per Person';
      case 'per_day':
        return 'Amount per Day';
      case 'per_person_day':
        return 'Amount per Person per Day';
      default:
        return 'Amount';
    }
  };

  const getInputSuffix = () => {
    return isPercentage ? '%' : currencySymbol;
  };

  const getPlaceholder = () => {
    switch (pricingType) {
      case 'percentage':
        return '10';
      case 'fixed':
        return '25';
      case 'per_person':
        return '15';
      case 'per_day':
        return '20';
      case 'per_person_day':
        return '5';
      default:
        return '0';
    }
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Value Input */}
      <div className="space-y-2">
        <Label htmlFor="pricing-value" className="text-sm font-medium text-slate-700">
          {getInputLabel()}
        </Label>
        
        <div className="relative">
          <Input
            id="pricing-value"
            type="number"
            value={value || ''}
            onChange={(e) => onValueChange(parseFloat(e.target.value) || 0)}
            placeholder={getPlaceholder()}
            min="0"
            max={isPercentage ? "100" : undefined}
            step={isPercentage ? "1" : "0.01"}
            className={cn(
              "pr-8",
              isPercentage ? "text-right" : "text-left"
            )}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500 pointer-events-none">
            {getInputSuffix()}
          </div>
        </div>
        
        {isPercentage && (
          <p className="text-xs text-slate-500">
            This will be {value}% of the base adventure price
          </p>
        )}
      </div>

      {/* Description Input */}
      <div className="space-y-2">
        <Label htmlFor="pricing-description" className="text-sm font-medium text-slate-700">
          Description (Optional)
        </Label>
        <Textarea
          id="pricing-description"
          value={description || ''}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder="Additional details about this pricing..."
          rows={2}
          className="resize-none"
        />
        <p className="text-xs text-slate-500">
          Help customers understand what this pricing covers
        </p>
      </div>
    </div>
  );
}
